import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { FaTimes, FaPlus, FaUserPlus, FaSearch, FaPen, FaEye } from "react-icons/fa";
import Layout from "../Component/Layout";
import { MASTER_API } from "../apiConfig";

// Uses the shared MASTER_API base from apiConfig.js instead of a
// hardcoded/localhost URL, so switching between local dev and the
// deployed CloudFront API only ever requires editing apiConfig.js.
// MASTER_API already ends in "/api" — don't add it again here.
const CREATE_USER_URL = `${MASTER_API}/user/create`;
const LIST_USERS_URL = `${MASTER_API}/user/list`;
const updateUserUrl = (userId) => `${MASTER_API}/user/${encodeURIComponent(userId)}`;

async function createUsers({ users }) {
  const token = localStorage.getItem("token");

  const res = await axios.post(
    CREATE_USER_URL,
    { users }, // each user carries the roleIds picked on the Roles tab
    {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    }
  );

  // Expected response shape:
  // { users: [ { email, users: [ { userId, name, password } ] }, ... ] }
  // — one entry per email submitted, each holding the accounts created
  // under it (usually one, more if you typed several comma-separated IDs).
  return res.data;
}

// Backs the "All users" table below the form. Expected response shape:
// { users: [ { id, email, userId, name, firstName, lastName, jobFunction, department, address, phone } ] } — no passwords included.
async function fetchAllUsers() {
  const token = localStorage.getItem("token");

  const res = await axios.get(LIST_USERS_URL, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });

  return res.data?.users || [];
}

// Every role, for the Roles tab dropdown. Expected response: [{ role_id, role_name, ... }]
async function fetchRoles() {
  const token = localStorage.getItem("token");

  const res = await axios.get(`${MASTER_API}/roles`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });

  return res.data || [];
}

// Roles currently assigned to one account. Expected response: [{ role_id, role_name }]
async function fetchUserRoles(userId) {
  const token = localStorage.getItem("token");

  const res = await axios.get(`${updateUserUrl(userId)}/roles`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });

  return res.data || [];
}

// Saves the editable profile fields of an existing account. user_id and
// email are never sent — user_id is the login key, so it's fixed for the
// account's lifetime, and email links accounts together. `profile` may also
// carry roleIds, which replaces the account's roles.
async function updateUser(userId, profile) {
  const token = localStorage.getItem("token");

  const res = await axios.patch(
    updateUserUrl(userId),
    profile,
    {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    }
  );

  return res.data;
}

const EMPTY_FORM = {
  userId: "",
  email: "",
  firstName: "",
  lastName: "",
  jobFunction: "",
  department: "",
  address: "",
  phone: ""
};

// Profile fields that can be edited after creation (user ID and email can't).
const EMPTY_PROFILE = {
  firstName: "",
  lastName: "",
  jobFunction: "",
  department: "",
  address: "",
  phone: ""
};

// [field, label, placeholder] — rendered in the View / Edit popup.
const PROFILE_TEXT_FIELDS = [
  ["firstName", "First name", "e.g. Jane", true],
  ["lastName", "Last name", "e.g. Smith", true],
  ["jobFunction", "Function", "e.g. Quality Engineer"],
  ["department", "Department", "e.g. Quality"]
];

// react-select styling so the role dropdown matches the form inputs. The
// menu is portalled to <body>, so it needs a zIndex above the popup overlay.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    fontSize: 13,
    borderRadius: 6,
    background: "#fafbfc",
    borderColor: state.isFocused ? "#93c5fd" : "#e5e9f0",
    boxShadow: "none",
    ":hover": { borderColor: "#93c5fd" }
  }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  option: (base) => ({ ...base, fontSize: 13 }),
  placeholder: (base) => ({ ...base, fontSize: 13 })
};

const formatNow = () =>
  new Date().toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });

const CreateUser = () => {

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [activeTab, setActiveTab] = useState("basic"); // "basic" | "roles"
  const [lastChanged, setLastChanged] = useState("");
  const [error, setError] = useState("");
  const [results, setResults] = useState(null); // [{ email, userId, name }]
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false); // create-user popup
  const [createRoleIds, setCreateRoleIds] = useState([]); // roles picked in the create popup
  const [allRoles, setAllRoles] = useState([]); // [{ role_id, role_name }] for the dropdowns

  // "All users" table state — separate from the create-form state above,
  // since this section just displays (and lets you rename) what's
  // already in the DB.
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");
  const [userQuery, setUserQuery] = useState("");

  // View / Edit popup for one account. `detail` is null when closed,
  // otherwise { mode: "view" | "edit", user }.
  const [detail, setDetail] = useState(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_PROFILE });
  const [savingEdit, setSavingEdit] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailTab, setDetailTab] = useState("basic"); // "basic" | "roles"
  const [detailRoleIds, setDetailRoleIds] = useState([]);
  const [originalRoleIds, setOriginalRoleIds] = useState([]); // roles the user had when the popup opened
  const [detailRolesLoading, setDetailRolesLoading] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false); // roles are only sent on save if they loaded
  const [notice, setNotice] = useState("");
  const detailReqRef = useRef(0); // ignores a late roles response after switching users

  const loadAllUsers = async () => {
    setUsersLoading(true);
    setUsersError("");
    try {
      const data = await fetchAllUsers();
      setAllUsers(data);
    } catch (err) {
      setUsersError(
        err?.response?.data?.message || err.message || "Failed to load users."
      );
    } finally {
      setUsersLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      setAllRoles(await fetchRoles());
    } catch (err) {
      console.error("FETCH ROLES ERROR:", err);
    }
  };

  useEffect(() => {
    loadAllUsers();
    loadRoles();
  }, []);

  // Client-side filter on user ID / first name / last name / email — fine
  // while the table stays small (internal tool user base).
  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(
      (u) =>
        u.userId?.toLowerCase().includes(q) ||
        u.firstName?.toLowerCase().includes(q) ||
        u.lastName?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
  }, [allUsers, userQuery]);

  const openDetail = async (u, mode) => {
    const requestId = ++detailReqRef.current;
    setEditForm({
      firstName: u.firstName || "",
      lastName: u.lastName || "",
      jobFunction: u.jobFunction || "",
      department: u.department || "",
      address: u.address || "",
      phone: u.phone || ""
    });
    setDetailError("");
    setNotice("");
    setDetailTab("basic");
    setDetailRoleIds([]);
    setOriginalRoleIds([]);
    setRolesLoaded(false);
    setDetailRolesLoading(true);
    setDetail({ mode, user: u });

    try {
      const rows = await fetchUserRoles(u.userId);
      if (requestId !== detailReqRef.current) return;
      const ids = rows.map((r) => r.role_id);
      setDetailRoleIds(ids);
      setOriginalRoleIds(ids);
      setRolesLoaded(true);
    } catch (err) {
      if (requestId !== detailReqRef.current) return;
      setDetailError(
        err?.response?.data?.message || err.message || "Couldn't load this user's roles."
      );
    } finally {
      if (requestId === detailReqRef.current) setDetailRolesLoading(false);
    }
  };

  // Blocked while a save is in flight so the request can't be orphaned.
  const closeDetail = () => {
    if (savingEdit) return;
    setDetail(null);
    setDetailError("");
  };

  const handleEditFieldChange = (field, value) =>
    setEditForm((prev) => ({ ...prev, [field]: value }));

  const handleSaveEdit = async () => {
    const f = Object.fromEntries(
      Object.entries(editForm).map(([k, v]) => [k, v.trim()])
    );

    if (!f.firstName) {
      setDetailError("First name is required.");
      setDetailTab("basic");
      return;
    }
    if (!f.lastName) {
      setDetailError("Last name is required.");
      setDetailTab("basic");
      return;
    }

    if (f.phone && !/^[+()\-\s\d]{6,20}$/.test(f.phone)) {
      setDetailError("Phone number can only contain digits, spaces, +, - and ( ).");
      setDetailTab("basic");
      return;
    }

    // A user who already has roles can't be left with none.
    if (rolesLoaded && detailRoleIds.length === 0 && originalRoleIds.length > 0) {
      setDetailError("Select at least one role, or cancel to keep the current roles.");
      setDetailTab("roles");
      return;
    }

    const { userId, email } = detail.user;
    setSavingEdit(true);
    setDetailError("");
    try {
      const payload = { ...f };
      if (rolesLoaded && detailRoleIds.length > 0) payload.roleIds = detailRoleIds;

      const saved = await updateUser(userId, payload);
      setAllUsers((prev) =>
        prev.map((u) =>
          u.userId === userId ? { ...u, ...f, name: saved?.name ?? u.name } : u
        )
      );
      setNotice(
        saved?.rolesChanged && saved?.emailSent === false
          ? `Roles were saved, but the email to ${email} couldn't be sent.`
          : ""
      );
      setDetail(null);
    } catch (err) {
      setDetailError(
        err?.response?.data?.message || err.message || "Failed to update user."
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const isViewMode = detail?.mode === "view";

  const roleOptions = allRoles.map((r) => ({ value: r.role_id, label: r.role_name }));
  // Dropdown to add roles, with the chosen roles listed underneath as
  // badges (each with an × to remove while editing). When `disabled` (view
  // mode) only the badge list is shown.
  const renderRoleField = (selectedIds, onChange, disabled, emptyText) => {
    const selected = selectedIds
      .map((id) => roleOptions.find((o) => o.value === id))
      .filter(Boolean);

    return (
      <>
        {!disabled && (
          <Select
            isMulti
            options={roleOptions}
            value={selected}
            onChange={(opts) => onChange((opts || []).map((o) => o.value))}
            placeholder={allRoles.length === 0 ? "No roles created yet" : "Select role(s)…"}
            controlShouldRenderValue={false}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            menuPortalTarget={document.body}
            styles={selectStyles}
          />
        )}
        <div style={{ ...styles.roleChips, marginTop: disabled ? 0 : 12 }}>
          {selected.length === 0 ? (
            <span style={styles.noRoles}>{emptyText}</span>
          ) : (
            selected.map((o) => (
              <span key={o.value} style={styles.roleChip}>
                {o.label}
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`Remove ${o.label}`}
                    style={styles.roleChipRemove}
                    onClick={() => onChange(selectedIds.filter((id) => id !== o.value))}
                  >
                    ×
                  </button>
                )}
              </span>
            ))
          )}
        </div>
      </>
    );
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setCreateRoleIds([]);
    setActiveTab("basic");
    setResults(null);
    setError("");
  };

  const openModal = () => {
    resetForm();
    setLastChanged(formatNow());
    setShowModal(true);
  };

  // Closing the popup always resets the form. Blocked while a create
  // request is in flight so the result can't be lost mid-submit.
  const closeModal = () => {
    if (submitting) return;
    setShowModal(false);
    resetForm();
  };

  // "Done" (shown after the account is created) just closes the popup —
  // the table behind it was already refreshed by loadAllUsers().
  const handleDone = closeModal;


  const handleFieldChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const validate = (f) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Letters, numbers, underscore, hyphen — no spaces, so it's easy to
    // type at login and safe to store as-is.
    const userIdPattern = /^[A-Za-z0-9_-]+$/;
    const phonePattern = /^[+()\-\s\d]{6,20}$/;

    if (!f.userId) return "User ID is required.";
    if (!userIdPattern.test(f.userId)) {
      return `"${f.userId}" isn't a valid user ID — letters, numbers, "_" and "-" only.`;
    }
    if (!f.email) return "Email address is required.";
    if (!emailPattern.test(f.email)) {
      return `"${f.email}" is not a valid email address.`;
    }
    if (!f.firstName) return "First name is required.";
    if (!f.lastName) return "Last name is required.";
    if (f.phone && !phonePattern.test(f.phone)) {
      return "Phone number can only contain digits, spaces, +, - and ( ).";
    }
    return "";
  };

  const handleSubmit = async () => {
    setError("");
    setResults(null);

    const f = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, v.trim()])
    );

    const validationError = validate(f);
    if (validationError) {
      setError(validationError);
      setActiveTab("basic");
      return;
    }

    if (createRoleIds.length === 0) {
      setError("Select at least one role.");
      setActiveTab("roles");
      return;
    }

    // One account per submit. `name` stays the combined display name so
    // the existing "Name" column / edit-name feature keeps working.
    const cleaned = [
      {
        email: f.email,
        userIds: [f.userId],
        name: `${f.firstName} ${f.lastName}`.trim(),
        firstName: f.firstName,
        lastName: f.lastName,
        jobFunction: f.jobFunction,
        department: f.department,
        address: f.address,
        phone: f.phone,
        roleIds: createRoleIds
      }
    ];

    setSubmitting(true);
    try {
      const data = await createUsers({ users: cleaned });

      // Each response entry is one email with possibly several accounts
      // created under it — flatten to one row per account for display.
      // The password isn't kept here at all: it's sent by email only,
      // never held in frontend state or shown in the UI.
      const flattened = (data?.users || []).flatMap((entry) =>
        (entry.users || []).map((acc) => ({
          email: entry.email,
          userId: acc.userId,
          name: acc.name || ""
        }))
      );

      setResults(
        flattened.length > 0
          ? flattened
          : cleaned.flatMap((r) =>
              r.userIds.map((id) => ({ email: r.email, userId: id, name: r.name }))
            )
      );
      setForm({ ...EMPTY_FORM });

      // The table below should reflect the accounts we just created,
      // without needing a manual page refresh.
      loadAllUsers();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div style={styles.pageHeaderRow}>
        <div>
          <div style={styles.pageTitle}>Create User</div>
          <div style={{ ...styles.pageSubtitle, marginBottom: 0 }}>
            View all accounts below, click Create to add new users, or use
            View / Edit on a row.
          </div>
        </div>
        <button
          type="button"
          style={{ ...styles.btnPrimary, cursor: "pointer" }}
          onClick={openModal}
        >
          <FaPlus size={11} style={{ marginRight: 6 }} />
          Create
        </button>
      </div>

      {/* All existing users */}
      <div style={styles.tableCard}>
        <div style={styles.tableHeaderRow}>
          <div style={styles.tableTitle}>All users</div>
          <div style={styles.headerActions}>
            <div style={styles.searchBox}>
              <FaSearch size={12} style={{ color: "#94a3b8" }} />
              <input
                type="text"
                value={userQuery}
                placeholder="Search by user ID, name, or email…"
                style={styles.searchInput}
                onChange={(e) => setUserQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {usersError && <div style={styles.errorBox}>{usersError}</div>}
        {notice && <div style={styles.errorBox}>{notice}</div>}

        {usersLoading ? (
          <div style={styles.emptyState}>Loading users…</div>
        ) : filteredUsers.length === 0 ? (
          <div style={styles.emptyState}>
            {allUsers.length === 0
              ? "No users found."
              : "No users match your search."}
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>User ID</th>
                <th style={styles.th}>First Name</th>
                <th style={styles.th}>Last Name</th>
                <th style={styles.th}>Email</th>
                <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id ?? `${u.email}-${u.userId}`}>
                  <td style={{ ...styles.td, fontFamily: "monospace" }}>{u.userId}</td>
                  <td style={styles.td}>{u.firstName || "—"}</td>
                  <td style={styles.td}>{u.lastName || "—"}</td>
                  <td style={styles.td}>{u.email}</td>
                  <td style={{ ...styles.td, textAlign: "right" }}>
                    <div style={styles.actionGroup}>
                      <button
                        type="button"
                        style={styles.smallBtnGhost}
                        onClick={() => openDetail(u, "view")}
                      >
                        <FaEye size={11} style={{ marginRight: 5 }} />
                        View
                      </button>
                      <button
                        type="button"
                        style={styles.smallBtnGhost}
                        onClick={() => openDetail(u, "edit")}
                      >
                        <FaPen size={10} style={{ marginRight: 5 }} />
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!usersLoading && allUsers.length > 0 && (
          <div style={styles.countHint}>
            Showing {filteredUsers.length} of {allUsers.length} account
            {allUsers.length > 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Create-user popup */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div
            style={styles.modalCard}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div style={styles.iconBox}>
                <FaUserPlus size={20} style={{ color: "#2563eb" }} />
              </div>
              <button
                type="button"
                style={styles.iconBtn}
                onClick={closeModal}
                aria-label="Close"
                disabled={submitting}
              >
                <FaTimes size={13} />
              </button>
            </div>

            {results ? (
              <div style={styles.resultsBox}>
                <div style={styles.resultsTitle}>Account created</div>
                {results.map((u) => (
                  <div key={`${u.email}-${u.userId}`} style={styles.resultRow}>
                    <div>
                      <div style={styles.resultEmail}>
                        {u.name ? `${u.name} — ${u.email}` : u.email}
                      </div>
                      <div style={styles.resultUserId}>User ID: {u.userId}</div>
                    </div>
                    <span style={styles.emailedTag}>Password emailed</span>
                  </div>
                ))}
                <div style={styles.resultsHint}>
                  The password is sent by email and isn't shown here. Log in
                  with the User ID above. The account has the roles you
                  selected.
                </div>
              </div>
            ) : (
              <>
                {/* Top: User ID / Email / Last changed */}
                <div style={styles.topGrid}>
                  <div>
                    <label style={styles.label}>User ID *</label>
                    <input
                      type="text"
                      value={form.userId}
                      placeholder="e.g. jsmith"
                      style={{ ...styles.input, ...styles.fullInput }}
                      onChange={(e) => handleFieldChange("userId", e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Email address *</label>
                    <input
                      type="email"
                      value={form.email}
                      placeholder="name@company.com"
                      style={{ ...styles.input, ...styles.fullInput }}
                      onChange={(e) => handleFieldChange("email", e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Last changed</label>
                    <input
                      type="text"
                      value={lastChanged}
                      readOnly
                      style={{ ...styles.input, ...styles.fullInput, ...styles.readOnlyInput }}
                    />
                  </div>
                </div>

                {/* Tabs */}
                <div style={styles.tabBar} role="tablist">
                  {[
                    { id: "basic", label: "Basic" },
                    { id: "roles", label: "Roles" }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      style={{
                        ...styles.tabBtn,
                        ...(activeTab === tab.id ? styles.tabBtnActive : {})
                      }}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {activeTab === "basic" && (
                  <div>
                    <div style={styles.sectionHeading}>General</div>
                    <div style={styles.formGrid}>
                      <div>
                        <label style={styles.label}>First name *</label>
                        <input
                          type="text"
                          value={form.firstName}
                          placeholder="e.g. Jane"
                          style={{ ...styles.input, ...styles.fullInput }}
                          onChange={(e) => handleFieldChange("firstName", e.target.value)}
                        />
                      </div>
                      <div>
                        <label style={styles.label}>Last name *</label>
                        <input
                          type="text"
                          value={form.lastName}
                          placeholder="e.g. Smith"
                          style={{ ...styles.input, ...styles.fullInput }}
                          onChange={(e) => handleFieldChange("lastName", e.target.value)}
                        />
                      </div>
                      <div>
                        <label style={styles.label}>Function</label>
                        <input
                          type="text"
                          value={form.jobFunction}
                          placeholder="e.g. Quality Engineer"
                          style={{ ...styles.input, ...styles.fullInput }}
                          onChange={(e) => handleFieldChange("jobFunction", e.target.value)}
                        />
                      </div>
                      <div>
                        <label style={styles.label}>Department</label>
                        <input
                          type="text"
                          value={form.department}
                          placeholder="e.g. Quality"
                          style={{ ...styles.input, ...styles.fullInput }}
                          onChange={(e) => handleFieldChange("department", e.target.value)}
                        />
                      </div>
                    </div>

                    <div style={styles.sectionHeading}>Communication</div>
                    <div style={styles.formGrid}>
                      <div>
                        <label style={styles.label}>Address</label>
                        <textarea
                          value={form.address}
                          placeholder="Street, city, state, postal code"
                          rows={2}
                          style={{ ...styles.input, ...styles.fullInput, resize: "vertical" }}
                          onChange={(e) => handleFieldChange("address", e.target.value)}
                        />
                      </div>
                      <div>
                        <label style={styles.label}>Phone number</label>
                        <input
                          type="tel"
                          value={form.phone}
                          placeholder="e.g. +91 98765 43210"
                          style={{ ...styles.input, ...styles.fullInput }}
                          onChange={(e) => handleFieldChange("phone", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "roles" && (
                  <div>
                    <label style={styles.label}>Role(s) *</label>
                    <div style={{ marginTop: 4 }}>
                      {renderRoleField(createRoleIds, setCreateRoleIds, false, "No roles selected yet.")}
                    </div>
                  </div>
                )}

                {error && <div style={styles.errorBox}>{error}</div>}
              </>
            )}

            <div style={styles.footer}>
              {results ? (
                <button type="button" style={styles.btnGhost} onClick={handleDone}>
                  Done
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    style={styles.btnGhost}
                    onClick={closeModal}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.btnPrimary,
                      opacity: submitting ? 0.7 : 1,
                      cursor: submitting ? "not-allowed" : "pointer"
                    }}
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? "Creating…" : "Create"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View / Edit popup */}
      {detail && (
        <div style={styles.modalOverlay}>
          <div
            style={styles.modalCard}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div style={styles.iconBox}>
                {isViewMode ? (
                  <FaEye size={18} style={{ color: "#2563eb" }} />
                ) : (
                  <FaPen size={16} style={{ color: "#2563eb" }} />
                )}
              </div>
              <button
                type="button"
                style={styles.iconBtn}
                onClick={closeDetail}
                aria-label="Close"
                disabled={savingEdit}
              >
                <FaTimes size={13} />
              </button>
            </div>

            <div style={styles.modalTitle}>
              {isViewMode ? "User details" : "Edit user"}
            </div>

            {/* User ID and email identify the account, so they're never editable */}
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>User ID</label>
                <input
                  type="text"
                  value={detail.user.userId || ""}
                  readOnly
                  style={{ ...styles.input, ...styles.fullInput, ...styles.readOnlyInput }}
                />
              </div>
              <div>
                <label style={styles.label}>Email address</label>
                <input
                  type="text"
                  value={detail.user.email || ""}
                  readOnly
                  style={{ ...styles.input, ...styles.fullInput, ...styles.readOnlyInput }}
                />
              </div>
            </div>
            {!isViewMode && (
              <div style={{ ...styles.modalHint, marginBottom: 12 }}>
                User ID and email can't be changed.
              </div>
            )}

            {/* Tabs */}
            <div style={styles.tabBar} role="tablist">
              {[
                { id: "basic", label: "Basic" },
                { id: "roles", label: "Roles" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={detailTab === tab.id}
                  style={{
                    ...styles.tabBtn,
                    ...(detailTab === tab.id ? styles.tabBtnActive : {})
                  }}
                  onClick={() => setDetailTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {detailTab === "basic" && (
              <div>
              <div style={styles.sectionHeading}>General</div>
              <div style={styles.formGrid}>
                {PROFILE_TEXT_FIELDS.map(([field, label, placeholder, required]) => (
                  <div key={field}>
                    <label style={styles.label}>
                      {label}
                      {required && !isViewMode ? " *" : ""}
                    </label>
                    <input
                      type="text"
                      value={editForm[field]}
                      placeholder={isViewMode ? "" : placeholder}
                      readOnly={isViewMode}
                      style={{
                        ...styles.input,
                        ...styles.fullInput,
                        ...(isViewMode ? styles.readOnlyInput : {})
                      }}
                      onChange={(e) => handleEditFieldChange(field, e.target.value)}
                    />
                  </div>
                ))}
              </div>

              <div style={styles.sectionHeading}>Communication</div>
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.label}>Address</label>
                  <textarea
                    value={editForm.address}
                    placeholder={isViewMode ? "" : "Street, city, state, postal code"}
                    rows={2}
                    readOnly={isViewMode}
                    style={{
                      ...styles.input,
                      ...styles.fullInput,
                      resize: "vertical",
                      ...(isViewMode ? styles.readOnlyInput : {})
                    }}
                    onChange={(e) => handleEditFieldChange("address", e.target.value)}
                  />
                </div>
                <div>
                  <label style={styles.label}>Phone number</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    placeholder={isViewMode ? "" : "e.g. +91 98765 43210"}
                    readOnly={isViewMode}
                    style={{
                      ...styles.input,
                      ...styles.fullInput,
                      ...(isViewMode ? styles.readOnlyInput : {})
                    }}
                    onChange={(e) => handleEditFieldChange("phone", e.target.value)}
                  />
                </div>
              </div>
              </div>
            )}

            {detailTab === "roles" && (
              <div>
                {detailRolesLoading ? (
                  <div style={styles.emptyState}>Loading roles…</div>
                ) : (
                  <>
                    <label style={styles.label}>Assigned role(s)</label>
                    <div style={{ marginTop: 4 }}>
                      {renderRoleField(
                        detailRoleIds,
                        setDetailRoleIds,
                        isViewMode || !rolesLoaded,
                        "No roles assigned."
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {detailError && <div style={styles.errorBox}>{detailError}</div>}

            <div style={styles.footer}>
              {isViewMode ? (
                <>
                  <button type="button" style={styles.btnGhost} onClick={closeDetail}>
                    Close
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.btnPrimary, cursor: "pointer" }}
                    onClick={() => setDetail((d) => ({ ...d, mode: "edit" }))}
                  >
                    <FaPen size={11} style={{ marginRight: 6 }} />
                    Edit
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    style={styles.btnGhost}
                    onClick={closeDetail}
                    disabled={savingEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.btnPrimary,
                      opacity: savingEdit ? 0.7 : 1,
                      cursor: savingEdit ? "not-allowed" : "pointer"
                    }}
                    onClick={handleSaveEdit}
                    disabled={savingEdit}
                  >
                    {savingEdit ? "Saving…" : "Save changes"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};


const styles = {
  pageHeaderRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 24
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 4
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#64748b",
    marginBottom: 24
  },
  card: {
    background: "#fff",
    borderRadius: 10,
    border: "1px solid #e5e9f0",
    padding: "24px 26px",
    maxWidth: 820
  },
  iconBox: {
    width: 34,
    height: 34,
    borderRadius: 8,
    background: "#e8f0fe",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8
  },
  field: { marginBottom: 18 },
  fieldHeaderRow: {
    display: "flex",
    gap: 8,
    marginBottom: 6
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 700,
    color: "#1e293b"
  },
  emailLabel: { flex: "1 1 auto" },
  userIdLabel: { flex: "0 0 220px" },
  nameLabel: { flex: "0 0 180px" },
  input: {
    padding: "7px 10px",
    border: "1px solid #e5e9f0",
    borderRadius: 6,
    fontSize: 13,
    outline: "none",
    background: "#fafbfc",
    boxSizing: "border-box"
  },
  emailInput: { flex: "1 1 auto", minWidth: 0 },
  userIdInput: { flex: "0 0 220px" },
  nameInput: { flex: "0 0 180px" },
  emailRow: {
    display: "flex",
    gap: 8,
    marginBottom: 10,
    alignItems: "center"
  },
  iconBtn: {
    width: 36,
    height: 36,
    flex: "0 0 36px",
    border: "1px solid #e5e9f0",
    background: "#fff",
    borderRadius: 7,
    cursor: "pointer",
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  addEmailBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "1px dashed #b9c3e0",
    color: "#2563eb",
    padding: "8px 12px",
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
    marginTop: 2
  },
  errorBox: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
    fontSize: 13,
    padding: "10px 12px",
    borderRadius: 7,
    marginBottom: 16
  },
  resultsBox: {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 8,
    padding: "14px 16px",
    marginBottom: 16
  },
  resultsTitle: {
    fontSize: 13.5,
    fontWeight: 700,
    color: "#15803d",
    marginBottom: 10
  },
  resultRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "7px 0",
    borderTop: "1px solid #dcf5e3"
  },
  resultEmail: { fontSize: 13, color: "#1e293b", fontWeight: 600 },
  resultUserId: { fontSize: 11.5, color: "#3f7a52", fontFamily: "monospace", marginTop: 2 },
  emailedTag: { fontSize: 12, color: "#15803d", fontStyle: "italic" },
  resultsHint: {
    fontSize: 11.5,
    color: "#3f7a52",
    marginTop: 10
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 4
  },
  btnGhost: {
    padding: "8px 16px",
    borderRadius: 7,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid #e5e9f0",
    background: "#fff",
    color: "#1e293b"
  },
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 16px",
    borderRadius: 7,
    fontSize: 13.5,
    fontWeight: 600,
    border: "none",
    background: "#2563eb",
    color: "#fff"
  },

  // Create-user popup form
  topGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1.4fr 1fr",
    gap: 10,
    marginBottom: 12
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 12
  },
  fullInput: { width: "100%", marginTop: 4 },
  readOnlyInput: { background: "#f1f5f9", color: "#64748b", cursor: "default" },
  tabBar: {
    display: "flex",
    gap: 4,
    borderBottom: "1px solid #e5e9f0",
    marginBottom: 12
  },
  tabBtn: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 600,
    color: "#64748b",
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    marginBottom: -1,
    cursor: "pointer"
  },
  tabBtnActive: { color: "#2563eb", borderBottom: "2px solid #2563eb" },
  sectionHeading: {
    fontSize: 11.5,
    fontWeight: 700,
    color: "#1e293b",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    paddingBottom: 4,
    marginBottom: 8,
    borderBottom: "1px solid #eef1f5"
  },

  // Create-user popup
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    zIndex: 1000
  },
  modalCard: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #e5e9f0",
    padding: "16px 20px",
    width: "100%",
    maxWidth: 620,
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 20px 50px rgba(15, 23, 42, 0.25)"
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start"
  },
  modalHint: {
    fontSize: 13,
    color: "#64748b",
    marginBottom: 18,
    lineHeight: 1.5
  },
  roleChips: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginBottom: 14
  },
  roleChip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 12px",
    borderRadius: 8,
    background: "#e8f0fe",
    border: "1px solid #c7d7fb",
    color: "#1d4ed8",
    fontSize: 13,
    fontWeight: 600
  },
  roleChipRemove: {
    background: "none",
    border: "none",
    color: "#1d4ed8",
    cursor: "pointer",
    fontSize: 15,
    lineHeight: 1,
    padding: 0
  },
  noRoles: { fontSize: 13, color: "#94a3b8" },
  sharedNote: {
    fontSize: 12.5,
    color: "#92400e",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 6,
    padding: "8px 10px"
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 14
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap"
  },

  // "All users" table section
  tableCard: {
    background: "#fff",
    borderRadius: 10,
    border: "1px solid #e5e9f0",
    padding: "22px 26px",
    width: "100%",
    boxSizing: "border-box"
  },
  tableHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
    flexWrap: "wrap"
  },
  tableTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#1e293b"
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    border: "1px solid #e5e9f0",
    borderRadius: 7,
    padding: "8px 12px",
    background: "#fafbfc",
    minWidth: 260
  },
  searchInput: {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 13.5,
    flex: 1,
    color: "#1e293b"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse"
  },
  th: {
    textAlign: "left",
    fontSize: 12.5,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.3,
    padding: "8px 10px",
    borderBottom: "2px solid #e5e9f0"
  },
  td: {
    fontSize: 13.5,
    color: "#1e293b",
    padding: "10px 10px",
    borderBottom: "1px solid #eef1f5"
  },
  editInput: {
    padding: "6px 8px",
    border: "1px solid #93c5fd",
    borderRadius: 6,
    fontSize: 13.5,
    outline: "none",
    background: "#fff",
    width: "100%",
    boxSizing: "border-box"
  },
  actionGroup: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 6
  },
  smallBtnGhost: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 6,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid #e5e9f0",
    background: "#fff",
    color: "#1e293b"
  },
  smallBtnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 6,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    background: "#2563eb",
    color: "#fff"
  },
  emptyState: {
    fontSize: 14,
    color: "#94a3b8",
    padding: "20px 0",
    textAlign: "center"
  },
  countHint: {
    fontSize: 12,
    color: "#94a3b8",
    marginTop: 14
  }
};

export default CreateUser;